export enum GenetecColors {
  blue = '#005197',
}

export enum PrimaryColors {
  main = '#22282f',
  light = '#363c43',
  dark = '#0e141b',
}

export enum SecondaryColors {
  main = '#00aeef',
  light = '#3dc1f3',
  lighter = '#66e0ff',
  dark = '#007fbc',
}

export enum BackgroundColors {
  light = '#f0f0f0',
  dark = '#2c3239',
}

export enum SurfaceColors {
  light = '#ffffff',
  dark = '#181e25',
}

export enum TextColors {
  light = '#f0f0f0',
  dark = '#181e25',
}

export enum TextOpacities {
  light1 = 0.95,
  light2 = 0.75,
  light3 = 0.49,
  light4 = 0.15,
  dark1 = 0.8,
  dark2 = 0.6,
  dark3 = 0.38,
  dark4 = 0.18,
}

export enum Theme {
  light = 'light',
  dark = 'dark',
}

export enum ColorNames {
  white = 'white',
  noBackgroundColor = 'no-background-color',
  eggshell = 'eggshell',
  mediumGray = 'medium-gray',
  charcoal = 'charcoal',
  darkGray = 'dark-gray',
  black = 'black',
  blue = 'blue',
}

export enum SpacingSizes {
  card = 'card',
  large = 'large',
  small = 'small',
  medium = 'medium',
  none = 'none',
}

export enum GlobalVariables {
  noBackgroundColor = 'transparent',
}

export enum MaxWidth {
  large = 'large',
  medium = 'medium',
  small = 'small',
}

export const maxWidthMap = {
  [SpacingSizes.large]: 1244,
  [SpacingSizes.medium]: 1044,
  [SpacingSizes.small]: 844,
};

export const spacingSizesMap = {
  [SpacingSizes.large]: 120,
  [SpacingSizes.medium]: 80,
  [SpacingSizes.small]: 40,
  [SpacingSizes.none]: 0,
};

export const textColorMap = {
  [Theme.light]: {
    default: TextColors.dark,
    secondary: SecondaryColors.dark,
    'secondary-light': SecondaryColors.main,
    'secondary-dark': PrimaryColors.dark,
    primary: PrimaryColors.main,
    'default-inverse': TextColors.light,
  },
  [Theme.dark]: {
    default: TextColors.light,
    secondary: SecondaryColors.main,
    'secondary-light': SecondaryColors.light,
    'secondary-dark': SecondaryColors.main,
    primary: TextColors.light,
    'default-inverse': TextColors.dark,
  },
};

export const backgroundColorMap = {
  [Theme.light]: {
    default: BackgroundColors.light,
    cardBox: SurfaceColors.light,
    surface: SurfaceColors.light,
    'surface-inverse': SurfaceColors.dark,
    chips: BackgroundColors.light,
    'chips-light': SurfaceColors.light,
    'default-inverse': BackgroundColors.dark,
  },
  [Theme.dark]: {
    default: BackgroundColors.dark,
    cardBox: PrimaryColors.light,
    surface: SurfaceColors.dark,
    'surface-inverse': SurfaceColors.light,
    chips: BackgroundColors.dark,
    'chips-light': BackgroundColors.dark,
    'default-inverse': BackgroundColors.light,
  },
};

export const opacityLevelsMap = {
  [Theme.light]: {
    0: 1,
    1: TextOpacities.light1,
    2: TextOpacities.light2,
    3: TextOpacities.light3,
    4: TextOpacities.light4,
  },
  [Theme.dark]: {
    0: 1,
    1: TextOpacities.dark1,
    2: TextOpacities.dark2,
    3: TextOpacities.dark3,
    4: TextOpacities.dark4,
  },
};

export const colorMap = {
  [ColorNames.white]: SurfaceColors.light,
  [ColorNames.noBackgroundColor]: GlobalVariables.noBackgroundColor,
  [ColorNames.eggshell]: BackgroundColors.light,
  [ColorNames.mediumGray]: PrimaryColors.light,
  [ColorNames.charcoal]: PrimaryColors.main,
  [ColorNames.darkGray]: SurfaceColors.dark,
  [ColorNames.black]: PrimaryColors.dark,
};

export const secondaryColorMap = {
  main: SecondaryColors.main,
  light: SecondaryColors.light,
  lighter: SecondaryColors.lighter,
  dark: SecondaryColors.dark,
};

export enum AdvancedLogicTypes {
  Is = 'is',
  IsNot = 'is_not',
  IsEmpty = 'is_empty',
  IsNotEmpty = 'is_not_empty',
  StartsWith = 'starts_with',
  NotStartsWith = 'not_starts_with',
  Contains = 'contains',
  NotContains = 'not_contains',
}

export enum AdvancedLogicOperators {
  And = 'and',
  Or = 'or',
}

export enum AdvancedLogicActions {
  AlwaysShow = 'always_show',
  Show = 'show',
  Hide = 'hide',
}

export enum MarketoControlsDistribution {
  SingleColumn = 'single-column',
  Default = 'default',
}

export const animationDuration = {
  primary: 750,
};

export const contentToCategoryMap = new Map<string, string>([
  ['customer story', 'industry'],
  ['blog', 'topic'],
  ['legal', 'legal'],
  ['podcast', 'podcast_series'],
  ['partner resources', 'category'],
  ['product releases', 'product-line'],
]);

export const formatPublicationDate = 'MMMM yyyy';

export const SDL_SEARCH_FIELDS = { model: {}, manufacturer: {}, equivalent_models: {} };

export const SDL_DEFAULT_QPARAMS = {
  q: '',
  page: '1',
  sortBy: 'manufacturer',
  sortType: 'asc',
  size: '10',
  category_type: 'camera',
};

export enum InsetTop {
  hero = 'hero',
  quote = 'quote',
  video = 'video',
  contentList = 'content-list',
  featuredContent = 'featured-content',
  imageGallery = 'image-gallery',
}

export enum LoadingStatusValues {
  loading = 'LOADING',
  ready = 'READY',
  initial = 'INITIAL',
}

export const DEFAULT_PRINT_STYLE = `
  @page { size: A4 portrait;  margin: 8mm; }
  @media print { body { color-adjust: exact; -webkit-print-color-adjust: exact; } }
  html, body { background-color: #fff !important; padding: 0; }
`;

export const IS_PREVIEW =
  window.isPreview !== undefined ? window.isPreview : window.parent.window !== window;

export const CMS_BASE_URL = window.cmsBaseUrl?.replace(/\/$/, '') || '';
export const SPA_BASE_URL = window.spaBaseUrl?.replace(/\/$/, '') || '';
export const BRXM_ENDPOINT = `${CMS_BASE_URL}/resourceapi`;

const LANG_PATH_PATTERN = /\/[a-z]{2}$/;
export const ROOT_BASE_URL = CMS_BASE_URL.replace(LANG_PATH_PATTERN, '');

// TODO: It works based on index.html, pending to make it work with SEO langCode.
export const LANG_ATTRIBUTE = document?.querySelector('html[lang]')?.getAttribute('lang') || 'en';

export const PRIMARY_FONT_FAMILY =
  window.primaryFontFamily || 'Circular, helvetica, arial, sans-serif';

export const SECONDARY_FONT_FAMILY =
  window.secondaryFontFamily || 'Garamond Premier Pro, Times New Roman, serif';

export const ADOBE_FONT_KIT_ID = window.adobeFontKitId || '';

export const GOOGLE_FONT_PRIMARY = window.googleFontPrimary || '';

export const GOOGLE_FONT_SECONDARY = window.googleFontSecondary || '';

export const SSO_CLIENT_ID = window.ssoClientId || '';
export const SSO_AUTHORITY = window.ssoAuthority || '';
export const SSO_BASE_URL = window.ssoBaseUrl || '';
export const SSO_REDIRECT_PATH = window.ssoRedirectPath || '';
export const SSO_POST_LOGOUT_REDIRECT_PATH = window.ssoPostLogoutRedirectPath || '';
export const SSO_SCOPE = window.ssoScope || '';
export const SSO_SILENT_REDIRECT_PATH = window.ssoSilentRedirectPath || '';
export const PORTAL_ROOT = window.ssoRoot || '/portal';
export const SSO_ROOT = `${SPA_BASE_URL}${PORTAL_ROOT}`;
export const SSO_RESOURCEAPI_ROOT = `${BRXM_ENDPOINT}${PORTAL_ROOT}`;
export const SSO_CALLBACK = `${SSO_BASE_URL}${SSO_REDIRECT_PATH}`;
export const SSO_LOGOUT_CALLBACK = `${SSO_BASE_URL}${SSO_POST_LOGOUT_REDIRECT_PATH}`;
export const SSO_UNAUTHORIZED_PATH = window.ssoUnauthorizedPath || '/unauthorized';
export const SSO_FORBIDDEN_PATH = window.ssoForbiddenPath || '/forbidden';
export const SSO_PENDING_APPROVAL_PATH = window.ssoPendingApprovalPath || '/pending-approval';
export const SSO_RESOURCEAPI_SSO_UNAUTHORIZED = `${BRXM_ENDPOINT}${SSO_UNAUTHORIZED_PATH}`;
export const SSO_RESOURCEAPI_SSO_FORBIDDEN = `${BRXM_ENDPOINT}${SSO_FORBIDDEN_PATH}`;
export const SSO_RESOURCEAPI_SSO_PENDING_APPROVAL = `${BRXM_ENDPOINT}${SSO_PENDING_APPROVAL_PATH}`;
export const SSO_CONTACT_REGISTRATION_PATH = `${SSO_ROOT}${
  window.ssoContactRegistrationPath || '/contact-registration'
}`;
export const SSO_INVITED_REGISTRATION_PATH = `${SSO_ROOT}${
  window.ssoInvitedRegistrationPath || '/invited-registration'
}`;
export const SSO_OLD_PORTAL_URL = window.ssoOldPortalUrl || 'https://portal.genetec.com/';
// Portal Homepage Redirects Configuration - loaded from Bloomreach value lists via Freemarker
export const PORTAL_HOMEPAGE_REDIRECTS = window.portalHomepageRedirects || {};
export const SSO_TOKEN_EXPIRED_EVENT_NAME = 'SSO_TOKEN_EXPIRED_EVENT';
export const SSO_TOKEN_EXPIRED_EVENT = new CustomEvent(SSO_TOKEN_EXPIRED_EVENT_NAME);
export const SSO_ANTI_CACHE = window.ssoAntiCache || false;

export const CUSTOM_FACETS = new Set(['is_genetec_certified', 'developed_by']);

export const ELASTIC_MAX_PAGES = 100;

export const HREFLANG_DEFAULT_LANG_ISO = 'en';

export const HEADER_HEIGHT = 58;

export const MAX_WIDTH_SEMI_XL = 1536;

export const POWERAPPS_BASE_URL = window.powerAppsBaseUrl || '';
export const POWERAPPS_SIGN_IN_URI = window.powerAppsSignInUri || '';
export const POWERAPPS_SIGN_OUT_URI = window.powerAppsSignOutUri || '';
export const POWERAPPS_MINUTES_EXPIRATION = Number(window.powerAppsMinutesExpiration || '0');

export const EXPERLOGIX_SIGN_IN_URI = window.experlogixSignInUri || '';
export const EXPERLOGIX_SIGN_OUT_URI = window.experlogixSignOutUri || '';
export const EXPERLOGIX_MINUTES_EXPIRATION = Number(window.experlogixMinutesExpiration || '0');

// Blog related content.
export const MAX_RELATED_CONTENT = 3;

export const DEBUG_MODE_ENABLED = window.debugModeEnabled || false;

export const IS_LOCAL = process.env.REACT_APP_IS_LOCAL || false;
